;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useEffect, useState } from 'react';
import { Button, FormGroup, Input, Label, ModalBody, ModalFooter } from 'reactstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { orderBy } from 'lodash';

import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter';
import { getCandidate, saveCandidate } from '../../../api/candidate';
import { useUser } from '../../../contexts/UserContext';
import { logSentryError } from '../../../utils/logSentry';
import FormAlert from '../../forms/FormAlert';
import contractTypeNodes, { contractTypeOptions } from '../../../utils/carerix/dataNodes/contract-type';
import { Dictionaries } from '../../../i18n/i18n-config';
import { useParams } from 'next/navigation';
import { getI18nUtils } from '../../../i18n/i18n-utils';

export const validationSchema = yup.object({
  contractType: yup.string().oneOf(contractTypeNodes).nullable(),
});

type JobPreferenceInputs = yup.InferType<typeof validationSchema>;

const initialValues: JobPreferenceInputs = { contractType: null };

type Props = {
  dictionary: Partial<Dictionaries>;
  next: () => void;
  prev: () => void;
};

function ProfileModalContractType({ next, prev, dictionary }: Props) {
  const { formatMessage } = getI18nUtils(dictionary);
  const { locale } = useParams();
  const { candidate, setCandidate } = useUser();
  const [alert, setAlert] = useState<{
    type: 'success' | 'warning' | 'danger';
    message: string;
  } | null>(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, actions) => {
      try {
        const data = { jobPreferences: { contractType: values.contractType } };
        await saveCandidate(data, 'acquaintance');
        // "silent" refresh of context
        const c = await getCandidate();
        setCandidate(c);
        actions.setSubmitting(false);
        actions.resetForm();
        next();
      } catch (e: any) {
        setAlert({ type: 'danger', message: 'internal-server-error' });
        actions.setSubmitting(false);
        logSentryError(e);
      }
    },
  });

  useEffect(() => {
    const { contractType } = candidate?.jobPreferences || {};
    formik.setValues({ contractType: contractType || initialValues.contractType });
  }, [candidate]);

  const getOptions = (nodes: any[]) =>
    orderBy(
      nodes.map((dl) => ({
        value: dl.value,
        label: capitalizeFirstLetter(dl.labels.find(({ language }) => language === locale)?.label),
      })),
      ['label']
    );
  const contracTypeSelect = getOptions(contractTypeOptions);

  const { isSubmitting, handleSubmit } = formik;
  const { onBlur, onChange, value } = formik.getFieldProps('contractType');

  return (
    <>
      <ModalBody>
        <div className="py-4">
          <h2 className="d-block mb-3">{formatMessage({ id: 'profile_form_contract_title' })}</h2>
          <p>{formatMessage({ id: 'profile_form_contract_intro' })}</p>

          <FormGroup tag="fieldset" disabled={isSubmitting} aria-busy={isSubmitting}>
            {contracTypeSelect.map((type) => (
              <FormGroup
                check
                key={`contractType_option_${type.value}`}
                className={`workpreference-option ps-5 border rounded mb-3 d-flex align-items-center
                ${type.value === value ? 'border-accent' : ''}
              `}
                style={{ cursor: 'pointer' }}
              >
                <Input
                  style={{ cursor: 'pointer' }}
                  name="contractType"
                  value={type.value}
                  id={type.value}
                  checked={type.value === value}
                  color="accent"
                  type="radio"
                  onChange={onChange}
                  onBlur={onBlur}
                />{' '}
                <Label check for={type.value} className="w-100 mb-0 p-3" style={{ cursor: 'pointer' }}>
                  {type.label}
                </Label>
              </FormGroup>
            ))}
          </FormGroup>
        </div>
      </ModalBody>
      <ModalFooter>
        <FormAlert dictionary={dictionary} className="flex-grow-1 mb-2" alert={alert} />
        <Button disabled={isSubmitting} color="primary" className="mt-5 w-100" onClick={() => handleSubmit()}>
          {formatMessage({ id: 'next' })}
        </Button>
        <Button disabled={isSubmitting} color="white" className="mt-3 mx-auto d-block" onClick={() => prev()}>
          {formatMessage({ id: 'previous' })}
        </Button>
      </ModalFooter>
    </>
  );
}

export default ProfileModalContractType;
